// src/hooks/usePostLogic.js
import { useEffect, useMemo, useRef, useState } from "react";
import { db, storage } from "../firebase";
import { deleteDoc, doc, onSnapshot, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";

// #931 — `ensureContentDoc`/`rateContent` importları KALDIRILDI: yazımı artık
// `StarRatingV2` yapıyor (ClipInFeed emsali). Bu hook yalnız sunum efekti.
import { isSaved as fsIsSaved, toggleSave as fsToggleSave } from "../savesClient";
import { successHaptic } from "../utils/haptics";
// `agg.avg` artık sunucuda SAKLANMIYOR (commutative yazım) → okuma anında türet.
import { aggNormalize } from "../utils/aggTuret";
import type { DocumentData } from "firebase/firestore";

/**
 * Gönderi dokümanı — gövde ~12 alanı okuyor (`authorId`, `mesaj`,
 * `mediaItems`, `mediaStoragePath`, `type`, `authorUsername`…) ve alan
 * kümesi post/klip türüne göre değişiyor → Firestore indeks imzası.
 */
export type GonderiKaydi = DocumentData;

/**
 * Yazarın GÖRÜNEN itibar ortalaması — ya gerçek bir sayı, ya HİÇBİR ŞEY.
 *
 * ── DOĞURAN KUSUR (masaüstü akış kartı, ölçüldü) ─────────────────────────
 * Eski hâl `… : 0` ile bitiyordu: alan yoksa sıfır. Rozet koşulsuz
 * basıldığı için `profileStar: null` / `profileStarState: "journey"` olan
 * (yani henüz puanı OLMAYAN) yazarın kartında **"★ 0.0"** görünüyordu —
 * hemen altında gerçek içerik puanı "4.5★ · 2 puan" dururken. Aynı kartta
 * iki çelişik sayı. Mobil ikizinde bu rozet HİÇ YOK.
 *
 * ── KANON ────────────────────────────────────────────────────────────────
 * YILDIZ_EKONOMISI / K1: *"Başlangıç Bayesyen μ=3.5'e demirli — UI'de sayı
 * DEĞİL, 'yolculuk başladı'."* Yani puanı olmayanın yerine 0 yazılmaz;
 * hiçbir şey yazılmaz. `0` bir ölçüm değil, ölçümün YOKLUĞUDUR — ikisini
 * aynı tipte taşımak (number) çağıranı yanıltır, bu yüzden dönüş `null`.
 *
 * Kabul: yalnız (0, 5] aralığındaki SONLU sayılar. `0`, negatif, `NaN`,
 * metin, `undefined` → `null`.
 */
export function gorunurItibar(reputation: unknown): number | null {
  if (!reputation || typeof reputation !== "object") return null;
  const ham = (reputation as { visible?: unknown }).visible;
  const sayi = typeof ham === "number" ? ham : typeof ham === "string" ? Number(ham) : NaN;
  if (!Number.isFinite(sayi) || sayi <= 0 || sayi > 5) return null;
  return sayi;
}

/**
 * Kart açıklamasının kısaltma eşiği (IG "…more" paritesi).
 *
 * ── NEDEN TEK KAYNAK ─────────────────────────────────────────────────────
 * Bu sabit + türetimi 2026-09-04'e kadar YALNIZ `Post.tsx` gövdesinde,
 * satır-içi duruyordu; mobil ikizi (`PostMobile.tsx`) açıklamayı hiç
 * kısaltmıyordu (IG denetimi P1-1). Mantık mobile taşınırken iki yere
 * kopyalanırsa eşik bir gün çatallanır ve "aynı gönderi iki cihazda iki
 * farklı yerde kesiliyor" kusuru doğar — kanonun `formatTimeAgoShort`
 * çatallanmasıyla (P2-1) aynı sınıf. Bu yüzden hook'a taşındı ve
 * `scripts/visual-eye/eye.js` tohumu da bu sayıya (140) göre uzun metin
 * yazıyor; sabiti değiştiren tohumu da güncellemelidir.
 */
export const CAPTION_LIMIT = 140;

/**
 * Açıklamayı kısaltır. Dönüş: kesme gerekip gerekmediği + önizleme metni.
 *
 * ⚠️ `preview` kısaltma GEREKMİYORSA tam metindir (çağıran iki dal yazmasın).
 * Kesme KARAKTER sayısıyla yapılır (IG'nin satır-tabanlı `line-clamp`'i
 * değil) — masaüstünde zaten yürürlükte olan davranış budur ve mobil onunla
 * BİREBİR aynı noktadan kesmelidir; ölçüt değiştirilseydi masaüstü kartın
 * ölçülmüş görünümü de değişirdi (bu turun kapsamı dışı).
 */
export function kisaltAciklama(ham: unknown): { needsClamp: boolean; preview: string; text: string } {
  const text = typeof ham === "string" ? ham : "";
  const needsClamp = text.length > CAPTION_LIMIT;
  return { needsClamp, preview: needsClamp ? text.slice(0, CAPTION_LIMIT).trim() : text, text };
}

// ⚠️ `post` NULL DEĞİL: gövde `post?.id` yazdığı yerler kadar `post.id`
// yazdığı yerler de var (`handleToggleSave`, `handleDelete`, denorm
// başlangıç state'i) — yani gerçek sözleşme "post HER ZAMAN var".
// `| null` yazmak gövdeyi yalanlar, `?.`leri "gereksiz" değil "tutarsız"
// yapardı.
export function usePostLogic(
  post: GonderiKaydi,
  aktifKullaniciId: string | null | undefined,
  onCommentClick?: (post: GonderiKaydi) => void,
) {
  // ----- STATE
  // PR-4: post dokümanında authorUsername/authorName/authorAvatarUrl varsa
  // bunları kullan (denorm). Yoksa users/{authorId} listener'ı devreye girer.
  const hasDenorm = Boolean(
    post?.authorUsername || post?.authorName || post?.authorAvatarUrl,
  );

  const [authorProfile, setAuthorProfile] = useState<any>(
    hasDenorm
      ? {
          kullaniciAdi: post.authorUsername || "",
          adSoyad: post.authorName || "",
          profilFoto: post.authorAvatarUrl || "",
        }
      : null,
  );
  const [isSaved, setIsSaved] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);
  const [showFullCaption, setShowFullCaption] = useState(false);
  // `content/{id}.agg` — alt alan kümesi tüketicide okunuyor (indeks imzası).
  // NOT: bu tip `Post.tsx:190` / `PostMobile.tsx:165`teki İKİ baseline
  // hatasını da (`Property 'avg' does not exist on type 'never'`)
  // düşürüyor; kalan `agg is possibly null` (satır 192/167) o dosyaların
  // kendi guard deseninden (`agg?.avg > 0 && … agg.avg`) geliyor.
  const [agg, setAgg] = useState<DocumentData | null>(null);

  // `Post.tsx:114` / `PostMobile.tsx:91` bu ref'i bir `<div>`e bağlıyor.
  const menuRef = useRef<HTMLDivElement | null>(null);
  const isOwner = post?.authorId === aktifKullaniciId;

  // ----- LISTENERS
  useEffect(() => {
    if (!post?.authorId) return;
    const userRef = doc(db, "users", post.authorId);
    const unsub = onSnapshot(userRef, (s) => {
      if (s.exists()) {
        setAuthorProfile(s.data());
      }
    }, () => { /* permission/rules hatasında sessiz boş kalma yerine no-op — uncaught önlenir */ });
    return () => unsub();
  }, [post?.authorId]);

  useEffect(() => {
    if (!post?.id) return;
    const contentRef = doc(db, "content", post.id);
    const unsub = onSnapshot(contentRef, (snap) => {
      const d = snap.exists() ? snap.data() : null;
      // COMMUTATIVE GEÇİŞİ (2026-08-19): `agg.avg` sunucuda ARTIK SAKLANMIYOR
      // (bölme içerdiği için `FieldValue.increment()` ile yazılamaz —
      // src/utils/aggTuret.ts). Post/PostMobile/PostDetailModalDesktop
      // `agg.avg > 0` guard'ıyla okuyor; türetme burada yapılmazsa yıldız
      // satırı sessizce KAYBOLURDU.
      setAgg(aggNormalize(d?.agg));
    }, () => setAgg(null));
    return () => unsub();
  }, [post?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const saved = await fsIsSaved(post?.id);
      if (!cancelled) setIsSaved(saved);
    })();
    return () => { cancelled = true; };
  }, [post?.id]);

  // Menü kapatma bağlayıcıları
  useEffect(() => {
    if (!optionsOpen) return;
    const onDown = (e: KeyboardEvent) => e.key === "Escape" && setOptionsOpen(false);
    // `contains` `Node` istiyor, `e.target` `EventTarget` — DOM API'sinin
    // dar sözleşmesine SALT tip düzeyinde köprü.
    const onClickAway = (e: MouseEvent) =>
      menuRef.current && !menuRef.current.contains(e.target as Node | null) && setOptionsOpen(false);
    const onScroll = () => setOptionsOpen(false);
    document.addEventListener("keydown", onDown);
    document.addEventListener("click", onClickAway);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("keydown", onDown);
      document.removeEventListener("click", onClickAway);
      window.removeEventListener("scroll", onScroll);
    };
  }, [optionsOpen]);

  // ----- türetilen
  // PR-31 — GOLD TEK KAYNAK: rozet YALNIZ Star Economy engine denorm'u
  // (users/{uid}.goldStar === true) ile gösterilir. Legacy reputation formülü
  // (badges.gold || visible>=4.5 && sample>=1000) KALDIRILDI — profil rozetiyle
  // AYNI kaynak → feed/profil çatallanması biter. authorProfile zaten
  // users/{authorId} doc'u (mevcut read) → EKSTRA READ YOK. visibleScore
  // (görünen ortalama) legacy reputation'dan okunmaya devam eder (Gold DEĞİL).
  const { visibleScore, showGold } = useMemo(() => {
    const gold = authorProfile?.goldStar === true;
    return { visibleScore: gorunurItibar(authorProfile?.reputation), showGold: gold };
  }, [authorProfile]);

  // ----- ACTIONS
  const handleDelete = async () => {
    if (!isOwner || !post?.id) return;
    if (!window.confirm("Bu gönderiyi silmek istediğinizden emin misiniz?")) return;
    try {
      // Carousel: tüm medya dosyalarını sil; tekil gönderide mediaItems
      // yoktur → mediaStoragePath fallback'i çalışır.
      // DENETIM P2 — sıra: ÖNCE doc, SONRA medya. Eski sıra (medya→doc)
      // deleteDoc düşerse feed'de 404 medyalı "kırık post" bırakıyordu;
      // orphan medya kırık posttan iyidir (best-effort temizlenir).
      // posterPath (video kapak görseli, NewPost.tsx captureVideoPosterBlob)
      // ayrıca silinir — yoksa post silinince Storage'da yetim kalır.
      const paths = Array.isArray(post.mediaItems) && post.mediaItems.length
        ? post.mediaItems.flatMap((it) => [it?.storagePath, it?.posterPath]).filter(Boolean)
        : (post.mediaStoragePath ? [post.mediaStoragePath] : []);
      await deleteDoc(doc(db, "posts", post.id));
      await Promise.all(
        paths.map((p) => deleteObject(ref(storage, p)).catch(() => {}))
      );
    } catch (e) {
      console.error("Gönderi silinirken hata:", e);
    }
  };

  // ----- IG paritesi: açıklama düzenleme (yalnız yazar; rules update'e izinli)
  const [editOpen, setEditOpen] = useState(false);
  const [editText, setEditText] = useState("");
  const openEdit = () => {
    if (!isOwner) return;
    setEditText(post?.mesaj || "");
    setEditOpen(true);
    setOptionsOpen(false);
  };
  const cancelEdit = () => setEditOpen(false);
  const saveEdit = async () => {
    if (!isOwner || !post?.id) return;
    try {
      await updateDoc(doc(db, "posts", post.id), {
        mesaj: editText,
        duzenlendiAt: serverTimestamp(),
      });
      setEditOpen(false);
    } catch (e) {
      console.error("Açıklama güncellenirken hata:", e);
    }
  };

  const handleToggleSave = async () => {
    setIsSaved((s) => !s);
    try {
      const { saved } = await fsToggleSave({
        contentId: post.id,
        type: post?.type || "post",
        authorId: post.authorId,
        mediaUrl: post.mediaUrl,
        caption: post.mesaj || "",
        isCarousel: Array.isArray(post?.mediaItems) && post.mediaItems.length > 1,
      });
      setIsSaved(saved);
    } catch (e) {
      setIsSaved((s) => !s);
      console.error("Kaydet sırasında hata:", e);
    }
  };

  const permalink = `${window.location.origin}/p/${post?.id || ""}`;

  // ── P1-4 + P1-5 (IG akış denetimi) ───────────────────────────────────────
  // ESKİ HÂL ve iki ölçülmüş kusuru:
  //   (1) Kart ✈️ doğrudan `navigator.share`a gidiyordu; masaüstünde o API
  //       yok → dala düşüp **`window.alert("Bağlantı kopyalandı")`** açıyordu
  //       (tarayıcının modal kutusu). Aynı eylem gönderi DETAYINDA ise nazik
  //       bir `role="status"` şeridi gösteriyordu → tek eylem, iki görünüm.
  //   (2) Detayda İKİ uçak ikonu vardı (Paylaş + "Mesaj olarak gönder") ve
  //       görsel olarak ayırt edilemiyorlardı.
  // YENİ HÂL (IG paritesi): tek ✈️ → **paylaş sheet'i**; DM'e gönderme,
  // bağlantı kopyalama ve (varsa) sistem paylaşımı o sheet'in satırları.
  // `handleShare` artık ağ/pano işi YAPMAZ, yalnız sheet'i açar — kopyalama
  // ve geri bildirim tek yerde (`components/ShareSheet.tsx` + ortak
  // `showToast`) toplanır. `window.alert` bu gövdede kalmadı.
  // ⚠️ İmza `async` kalmadı ama çağıranlar `onClick={handleShare}` diyor;
  // dönüş değeri hiçbir yerde beklenmiyordu (ölçüldü: Post.tsx:280,
  // PostMobile.tsx:233) → davranış deltası yok.
  const [shareOpen, setShareOpen] = useState(false);
  const handleShare = () => setShareOpen(true);
  /** DM eki — `services/dm.shareToDm` sözleşmesi (type/id/thumbUrl/caption/authorId). */
  const shareAttachment = useMemo(
    () => ({
      type: "post" as const,
      id: String(post?.id || ""),
      thumbUrl: String(
        post?.mediaItems?.[0]?.url || post?.mediaUrl || post?.gorselURL || "",
      ),
      caption: String(post?.mesaj || post?.aciklama || post?.caption || "").slice(0, 200),
      authorId: String(post?.authorId || ""),
    }),
    [post?.id, post?.mediaItems, post?.mediaUrl, post?.gorselURL, post?.mesaj, post?.aciklama, post?.caption, post?.authorId],
  );

  const handleToggleComments = async () => {
    if (!isOwner || !post?.id) return;
    try {
      await updateDoc(doc(db, "posts", post.id), { yorumlarKapali: !post?.yorumlarKapali });
      setOptionsOpen(false);
    } catch (e) {
      console.error("Yorumlar durumunu güncellerken hata:", e);
    }
  };

  const handleGoToPost = () => onCommentClick?.(post);

  // PR-29 — KİLİTLİ ÜRÜN KARARI: çift-dokun OTOMATİK 5★ YAZMAZ; puan
  // popover'ını açar, kullanıcı 1-5★'ı bilinçli seçer (V3 ile aynı davranış).
  // Yıldız patlaması artık seçim BAŞARISINDA oynar (handleRate içinde).
  // Tek dokunuş 280ms gecikmeyle yorumları açar (çift dokunuşla çakışmasın).
  const [showBurst, setShowBurst] = useState(false);
  const [rateOpenNonce, setRateOpenNonce] = useState(0);

  // #931 — YAZIM ARTIK BURADA DEĞİL, `StarRatingV2`de (ClipInFeed emsali).
  // Eskiden Post/PostMobile `contentId`/`authorId`i KASITLI OLARAK vermiyordu
  // ("çift yazım olmasın" diye) ve yazımı bu callback yapıyordu. Bedeli
  // ölçüldü (#931): bileşen "yazan ben değilim" sanıp kendi `myVal`ını
  // GÜNCELLEMİYOR → tetik «Yıldız ver»de kalıyor, popover yeniden açılınca
  // «Puanını seç (1–5)» diyordu. Artık bileşen `contentId`+`authorId` alıyor
  // ve kendisi yazıyor (`rateContent` `ensureContentDoc`u ZATEN çağırır) —
  // bu callback yalnız BAŞARIDAN SONRA çalışan SUNUM efektidir.
  // ⚠️ ÇİFT YAZIM YOK: bu gövdede tek bir Firestore çağrısı kalmadı.
  const handleRate = (_value: number) => {
    if (!post?.id) return;
    // PR-29 — yıldız patlaması: yalnız BAŞARILI puan yazımından sonra
    // (StarRatingV2 `onRated`ı yalnız yazım başarılıysa çağırır).
    setShowBurst(true);
    setTimeout(() => setShowBurst(false), 900);
  };

  // `setTimeout` dönüşü ortama göre değişir — `ReturnType` ile türetildi.
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `?? undefined`: `clearTimeout(null)` ile `clearTimeout(undefined)` ikisi
  // de no-op — davranış aynı, yalnız imza `null` kabul etmiyor.
  useEffect(() => () => clearTimeout(tapTimerRef.current ?? undefined), []);
  const handleMediaTap = () => {
    if (tapTimerRef.current) {
      clearTimeout(tapTimerRef.current);
      tapTimerRef.current = null;
      successHaptic();
      setRateOpenNonce(Date.now());
    } else {
      tapTimerRef.current = setTimeout(() => {
        tapTimerRef.current = null;
        onCommentClick?.(post);
      }, 280);
    }
  };

  return {
    // state
    authorProfile, isSaved, optionsOpen, setOptionsOpen,
    isMediaLoaded, setIsMediaLoaded, showFullCaption, setShowFullCaption, agg,
    // caption edit
    editOpen, editText, setEditText, openEdit, cancelEdit, saveEdit,
    // double-tap (PR-29: popover açar, otomatik yazmaz)
    showBurst, handleMediaTap, rateOpenNonce,
    // refs
    menuRef,
    // derived
    isOwner, visibleScore, showGold,
    // paylaş sheet'i (P1-4/P1-5) — kart `<ShareSheet>`i kendisi render eder
    shareOpen, setShareOpen, permalink, shareAttachment,
    // actions
    handleDelete, handleToggleSave, handleShare, handleToggleComments, handleGoToPost, handleRate,
  };
}
